import * as React from 'react';
import { FunctionComponent, useMemo, useState } from 'react';
import { HTMLEditorQuill } from '../HTMLEditorQuill/HTMLEditorQuill';
import { AppUser } from 'Src/views/App/App';
import {
  buildGmEmailHeaderHtml,
  GmEmailAlignment,
  GmEmailHeaderNamePlacement
} from 'Src/helpers/gmEmailTemplate/gmEmailTemplate';
import './GmEmailTemplateForm.scss';

export interface GmEmailTemplateSettings {
  gmEmailHeaderColor: string;
  gmEmailHeaderEnabled: boolean;
  gmEmailHeaderLogoEnabled: boolean;
  gmEmailHeaderNameEnabled: boolean;
  gmEmailHeaderLogoAlignment: GmEmailAlignment;
  gmEmailHeaderNamePlacement: GmEmailHeaderNamePlacement;
  gmEmailHeaderNameAlignment: GmEmailAlignment;
  gmEmailFooterHtml: string;
  gmEmailFooterBackgroundColor: string;
  gmEmailFooterTextColor: string;
  gmEmailFooterAlignment: GmEmailAlignment;
}

interface Props {
  user?: AppUser;
  imageUploader?: (image: File) => Promise<any>;
  settings: GmEmailTemplateSettings;
  schoolName: string;
  schoolLogo: string;
  isSaving: boolean;
  onSave: (settings: GmEmailTemplateSettings) => void;
  onCancel?: () => void;
}

const HEADER_COLORS = [
  '#1e3a5f',
  '#12386b',
  '#227ebd',
  '#0f7a5a',
  '#2f9e5a',
  '#0f8a8a',
  '#7b5bd6',
  '#a01d2e',
  '#c85a1e',
  '#1b2430'
];
const FOOTER_BG_COLORS = ['#f8f9fa', '#eef2f6', '#e9f1fa', '#f3eefb', '#fdf0dd', '#1b2430', '#12386b'];
const TEXT_COLORS = ['#64748b', '#334155', '#1b2430', '#ffffff', '#227ebd'];

const normalise = (settings: GmEmailTemplateSettings): GmEmailTemplateSettings => ({
  gmEmailHeaderColor: settings.gmEmailHeaderColor || '#1e3a5f',
  gmEmailHeaderEnabled: settings.gmEmailHeaderEnabled !== false,
  gmEmailHeaderLogoEnabled: settings.gmEmailHeaderLogoEnabled !== false,
  gmEmailHeaderNameEnabled: settings.gmEmailHeaderNameEnabled !== false,
  gmEmailHeaderLogoAlignment: settings.gmEmailHeaderLogoAlignment || 'left',
  gmEmailHeaderNamePlacement: settings.gmEmailHeaderNamePlacement || 'below',
  gmEmailHeaderNameAlignment: settings.gmEmailHeaderNameAlignment || 'left',
  gmEmailFooterHtml: settings.gmEmailFooterHtml || '',
  gmEmailFooterBackgroundColor: settings.gmEmailFooterBackgroundColor || '#f8f9fa',
  gmEmailFooterTextColor: settings.gmEmailFooterTextColor || '#64748b',
  gmEmailFooterAlignment: settings.gmEmailFooterAlignment || 'left'
});

const isLight = (hex: string): boolean => {
  const value = (hex || '').replace('#', '');
  if (value.length < 6) return true;
  const r = parseInt(value.substr(0, 2), 16);
  const g = parseInt(value.substr(2, 2), 16);
  const b = parseInt(value.substr(4, 2), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 186;
};

const icon = (path: string, size = 16) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={path} />
  </svg>
);

const objectPositionIcon = (position: GmEmailAlignment) => {
  const x = position === 'left' ? 4 : position === 'center' ? 9 : 14;
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <rect x={x} y="8" width="6" height="8" rx="1" fill="currentColor" />
    </svg>
  );
};

const namePlacementIcon = (placement: GmEmailHeaderNamePlacement) => {
  const logo = (x: number, y: number, width: number, height: number) => (
    <rect x={x} y={y} width={width} height={height} rx="1" fill="none" stroke="currentColor" strokeWidth="1.6" />
  );
  const name = (x: number, y: number, width: number, height: number) => (
    <rect x={x} y={y} width={width} height={height} rx="1" fill="currentColor" />
  );
  const parts = {
    below: [logo(7, 2, 10, 8), name(3, 14, 18, 5)],
    above: [name(3, 3, 18, 5), logo(7, 12, 10, 8)],
    right: [logo(2, 6, 9, 11), name(13, 10, 9, 4)],
    left: [name(2, 10, 9, 4), logo(13, 6, 9, 11)]
  };

  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      {parts[placement]}
    </svg>
  );
};

const ICONS = {
  check: 'M20 6 9 17l-5-5',
  eye: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
  undo: 'M3 7v6h6M3 13a9 9 0 1 0 3-7.7L3 8',
  alignLeft: 'M3 6h18M3 12h12M3 18h15',
  alignCenter: 'M3 6h18M6 12h12M4 18h16',
  alignRight: 'M3 6h18M9 12h12M6 18h15'
};

export const GmEmailTemplateForm: FunctionComponent<Props> = ({
  user,
  imageUploader,
  settings,
  schoolName,
  schoolLogo,
  isSaving,
  onSave,
  onCancel
}) => {
  const initial = useMemo(() => normalise(settings), [settings]);
  const [form, setForm] = useState<GmEmailTemplateSettings>(initial);
  const [activeSection, setActiveSection] = useState<'header' | 'footer'>('header');

  const update = (patch: Partial<GmEmailTemplateSettings>) => setForm(prev => ({ ...prev, ...patch }));
  const reset = () => setForm(initial);
  const isDirty = JSON.stringify(form) !== JSON.stringify(initial);
  const logoUrl = schoolLogo && schoolLogo.startsWith('//') ? `https:${schoolLogo}` : schoolLogo;
  const headerHtml = buildGmEmailHeaderHtml(form, schoolName, logoUrl);

  const swatches = (field: keyof GmEmailTemplateSettings, colours: string[]) => {
    const value = String(form[field] || '');
    return (
      <div className="gmTemplateSwatches">
        {colours.map(colour => {
          const selected = value.toLowerCase() === colour.toLowerCase();
          return (
            <button
              key={colour}
              type="button"
              className={selected ? 'gmTemplateSwatch gmTemplateSwatch--selected' : 'gmTemplateSwatch'}
              style={{ backgroundColor: colour, color: isLight(colour) ? '#1b2430' : '#ffffff' }}
              title={colour}
              onClick={() => update({ [field]: colour } as Partial<GmEmailTemplateSettings>)}
              disabled={isSaving}
            >
              {selected ? icon(ICONS.check, 14) : null}
            </button>
          );
        })}
        <label className="gmTemplateCustomColour">
          <span style={{ backgroundColor: value }} />
          <input
            type="text"
            value={value}
            onChange={event => update({ [field]: event.target.value } as Partial<GmEmailTemplateSettings>)}
            disabled={isSaving}
          />
        </label>
      </div>
    );
  };

  const toggleRow = (title: string, hint: string, checked: boolean, onChange: () => void) => (
    <div className="gmTemplateToggleRow">
      <div>
        <div className="gmTemplateToggleRow__title">{title}</div>
        <div className="gmTemplateToggleRow__hint">{hint}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={checked ? 'gmTemplateSwitch gmTemplateSwitch--on' : 'gmTemplateSwitch'}
        onClick={onChange}
        disabled={isSaving}
      >
        <span />
      </button>
    </div>
  );

  const segmented = <T extends string>(
    value: T,
    options: { value: T; label?: string; title?: string; iconPath?: string; iconNode?: React.ReactNode }[],
    onChange: (value: T) => void
  ) => (
    <div className="gmTemplateSegmented">
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          className={value === option.value ? 'gmTemplateSegment gmTemplateSegment--active' : 'gmTemplateSegment'}
          title={option.title || option.label}
          onClick={() => onChange(option.value)}
          disabled={isSaving}
        >
          {option.iconNode || (option.iconPath ? icon(option.iconPath, 16) : null)}
          {option.label ? <span>{option.label}</span> : null}
        </button>
      ))}
    </div>
  );

  const alignmentOptions = [
    { value: 'left' as GmEmailAlignment, title: 'Left', iconPath: ICONS.alignLeft },
    { value: 'center' as GmEmailAlignment, title: 'Centre', iconPath: ICONS.alignCenter },
    { value: 'right' as GmEmailAlignment, title: 'Right', iconPath: ICONS.alignRight }
  ];
  const logoAlignmentOptions = [
    { value: 'left' as GmEmailAlignment, title: 'Left', iconNode: objectPositionIcon('left') },
    { value: 'center' as GmEmailAlignment, title: 'Centre', iconNode: objectPositionIcon('center') },
    { value: 'right' as GmEmailAlignment, title: 'Right', iconNode: objectPositionIcon('right') }
  ];
  const namePlacementOptions = [
    { value: 'below' as GmEmailHeaderNamePlacement, label: 'Below', iconNode: namePlacementIcon('below') },
    { value: 'above' as GmEmailHeaderNamePlacement, label: 'Above', iconNode: namePlacementIcon('above') },
    { value: 'right' as GmEmailHeaderNamePlacement, label: 'Right', iconNode: namePlacementIcon('right') },
    { value: 'left' as GmEmailHeaderNamePlacement, label: 'Left', iconNode: namePlacementIcon('left') }
  ];

  const previewFooter = form.gmEmailFooterHtml
    ? form.gmEmailFooterHtml
    : `${schoolName || 'School'}<br />You are receiving this because you are linked to a pupil at our school.`;

  return (
    <div className="gmTemplateSettings">
      <div className="gmTemplateSettings__main">
        <div className="gmTemplateSettings__controls schoolSettingsCard">
          <div className="gmTemplateSettings__tabs" role="tablist" aria-label="Email branding sections">
            <button
              type="button"
              className={
                activeSection === 'header'
                  ? 'gmTemplateSettings__tab gmTemplateSettings__tab--active'
                  : 'gmTemplateSettings__tab'
              }
              onClick={() => setActiveSection('header')}
            >
              Email header
            </button>
            <button
              type="button"
              className={
                activeSection === 'footer'
                  ? 'gmTemplateSettings__tab gmTemplateSettings__tab--active'
                  : 'gmTemplateSettings__tab'
              }
              onClick={() => setActiveSection('footer')}
            >
              Email footer
            </button>
          </div>

          <div className="gmTemplateSettings__panel">
            {activeSection === 'header' && (
              <div className="gmTemplateSettings__section">
                {toggleRow('Show email header', 'Include the branded header in emails', form.gmEmailHeaderEnabled, () =>
                  update({ gmEmailHeaderEnabled: !form.gmEmailHeaderEnabled })
                )}

                {form.gmEmailHeaderEnabled && (
                  <div className="gmTemplateField">
                    <label>Header background colour</label>
                    {swatches('gmEmailHeaderColor', HEADER_COLORS)}
                  </div>
                )}

                {form.gmEmailHeaderEnabled &&
                  toggleRow('Show school logo', 'In the email header', form.gmEmailHeaderLogoEnabled, () =>
                    update({ gmEmailHeaderLogoEnabled: !form.gmEmailHeaderLogoEnabled })
                  )}
                {form.gmEmailHeaderEnabled && !logoUrl && form.gmEmailHeaderLogoEnabled && (
                  <div className="gmTemplateHint">No logo uploaded. Add one in School settings - Profile.</div>
                )}
                {form.gmEmailHeaderEnabled && form.gmEmailHeaderLogoEnabled && (
                  <div className="gmTemplateField">
                    <label>Logo alignment</label>
                    {segmented(form.gmEmailHeaderLogoAlignment, logoAlignmentOptions, value =>
                      update({ gmEmailHeaderLogoAlignment: value })
                    )}
                  </div>
                )}

                {form.gmEmailHeaderEnabled && <div className="gmTemplateDivider" />}

                {form.gmEmailHeaderEnabled &&
                  toggleRow('Show school name', 'In the email header', form.gmEmailHeaderNameEnabled, () =>
                    update({ gmEmailHeaderNameEnabled: !form.gmEmailHeaderNameEnabled })
                  )}
                {form.gmEmailHeaderEnabled && form.gmEmailHeaderNameEnabled && (
                  <>
                    <div className="gmTemplateField">
                      <label>Name placement</label>
                      {segmented(form.gmEmailHeaderNamePlacement, namePlacementOptions, value =>
                        update({ gmEmailHeaderNamePlacement: value })
                      )}
                    </div>
                    <div className="gmTemplateField">
                      <label>Name alignment</label>
                      {segmented(form.gmEmailHeaderNameAlignment, alignmentOptions, value =>
                        update({ gmEmailHeaderNameAlignment: value })
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeSection === 'footer' && (
              <div className="gmTemplateSettings__section">
                <div className="gmTemplateField">
                  <label>Background colour</label>
                  {swatches('gmEmailFooterBackgroundColor', FOOTER_BG_COLORS)}
                </div>
                <div className="gmTemplateField">
                  <label>Text colour</label>
                  {swatches('gmEmailFooterTextColor', TEXT_COLORS)}
                </div>
                <div className="gmTemplateField">
                  <label>Content alignment</label>
                  {segmented(form.gmEmailFooterAlignment, alignmentOptions, value =>
                    update({ gmEmailFooterAlignment: value })
                  )}
                </div>
                <div className="gmTemplateField gmTemplateField--editor">
                  <label>Footer text</label>
                  <HTMLEditorQuill
                    user={user}
                    imageUploader={imageUploader}
                    onEmailBodyChange={val => update({ gmEmailFooterHtml: val })}
                    value={form.gmEmailFooterHtml}
                    isEnabled={!isSaving}
                    placeholder="Address, unsubscribe note or any legal text. Each line is a paragraph."
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <aside className="gmTemplatePreviewPane">
          <div className="gmTemplatePreviewPane__label">{icon(ICONS.eye, 14)} Live preview</div>
          <div className="gmTemplateEmailPreview">
            {form.gmEmailHeaderEnabled && (
              <div className="gmTemplateEmailPreview__header" style={{ backgroundColor: form.gmEmailHeaderColor }}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: headerHtml || '<span style="color:rgba(255,255,255,.58);font-size:13px;">Header</span>'
                  }}
                />
              </div>
            )}
            <div className="gmTemplateEmailPreview__body">
              <div className="gmTemplateEmailPreview__title">End of term letter</div>
              <div className="gmTemplateEmailPreview__line" />
              <div className="gmTemplateEmailPreview__line gmTemplateEmailPreview__line--medium" />
              <div className="gmTemplateEmailPreview__line gmTemplateEmailPreview__line--short" />
              <button type="button" style={{ backgroundColor: form.gmEmailHeaderColor }}>
                View details
              </button>
            </div>
            <div
              className="gmTemplateEmailPreview__footer"
              style={{
                backgroundColor: form.gmEmailFooterBackgroundColor,
                color: form.gmEmailFooterTextColor,
                textAlign: form.gmEmailFooterAlignment
              }}
              dangerouslySetInnerHTML={{ __html: previewFooter }}
            />
          </div>
        </aside>
      </div>

      {isDirty && (
        <div className="gmTemplateSaveBar">
          <span className="gmTemplateSaveBar__status">You have unsaved changes</span>
          <div className="gmTemplateSaveBar__actions">
            <button
              type="button"
              className="schoolSettingsButton schoolSettingsButton--secondary"
              onClick={reset}
              disabled={isSaving}
            >
              {icon(ICONS.undo, 15)} Discard
            </button>
            {onCancel && (
              <button
                type="button"
                className="schoolSettingsButton schoolSettingsButton--secondary"
                onClick={onCancel}
                disabled={isSaving}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              className="schoolSettingsButton schoolSettingsButton--primary"
              onClick={() => onSave(form)}
              disabled={isSaving}
            >
              {icon(ICONS.check, 15)} {isSaving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

GmEmailTemplateForm.displayName = 'GmEmailTemplateForm';
